import React, { useEffect, useMemo, useState } from 'react';

import cn from 'classnames';

import { useTranslation } from 'next-i18next';
import Image from 'components/Image';
import IconButton from 'components/IconButton';
import { ButtonV3 } from 'components/ComponentV2';
import { useResponsive } from 'hooks/useResponsive';
import { useCommonList } from 'lib/contexts/common-list-context';

import styles from './WelcomePopup.module.scss';

interface WelcomePopupProps {
  onClose: () => void;
}

const WelcomePopup: React.FC<WelcomePopupProps> = (props) => {
  const { onClose } = props;
  const screens = useResponsive();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [t] = useTranslation('candidate-dashboard');
  const [tCommon] = useTranslation('common');
  const {
    configData: { opportunity_count },
    isConfigDataLoading,
  } = useCommonList();

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const stepsData = useMemo(
    () => [
      {
        title: <>{t('welcome-info.step1-text-heading')}</>,
        imageSrc: '/images/welcome_candidate_step_1.png',
        stepInfo: <>{t('welcome-info.step1-text')}</>,
      },
      {
        title: <>{t('welcome-info.step2-text-heading')}</>,
        imageSrc: '/images/welcome_candidate_step_2.png',
        stepInfo: (
          <p
            dangerouslySetInnerHTML={{ __html: t('welcome-info.step2-text') }}
          />
        ),
      },
      {
        title: <>{t('welcome-info.step3-text-heading')}</>,
        imageSrc: '/images/welcome_candidate_step_3.png',
        stepInfo: (
          <>
            <p
              dangerouslySetInnerHTML={{ __html: t('welcome-info.step3-text') }}
            />
          </>
        ),
      },
      {
        title: <>{t('welcome-info.step4-text-heading')}</>,
        imageSrc: '/images/welcome_candidate_step_4.png',
        stepInfo: <>{t('welcome-info.step4-text')}</>,
      },
      {
        title: <>{t('welcome-info.step5-text-heading')}</>,
        imageSrc: '/images/welcome_candidate_step_5.png',
        stepInfo: <>{t('welcome-info.step5-text')}</>,
      },
      ...(isConfigDataLoading
        ? []
        : [
            {
              title: (
                <>
                  {t('welcome-info.step6-text-heading', { opportunity_count })}
                </>
              ),
              imageSrc: '/images/welcome_candidate_step_6.png',
              stepInfo: (
                <>{t('welcome-info.step6-text', { opportunity_count })}</>
              ),
            },
          ]),
    ],
    [isConfigDataLoading]
  );

  const changeStep = (step: number) => () => {
    setCurrentIndex(step);
  };

  const isLastStep = useMemo(
    () => currentIndex === stepsData.length - 1,
    [currentIndex, stepsData]
  );

  return (
    <div className={styles.WelcomePopup}>
      <div className={styles.popupScreen}>
        <div className={styles.popupScreenHeader}>
          <p>{stepsData[currentIndex].title}</p>
          <IconButton
            iconName="close"
            size="small"
            className={styles.closeIcon}
            onClick={onClose}
          />
        </div>
        <div className={styles.popupScreenContent}>
          <Image
            src={stepsData[currentIndex].imageSrc}
            height={screens.sm ? 137 : 180}
            className={styles.image}
          />
          <p className={styles.stepInfo}>{stepsData[currentIndex].stepInfo}</p>
          <div className={styles.stepsIndicator}>
            {Array.from(Array(stepsData.length).keys()).map((count) => (
              <div
                key={count}
                className={cn(styles.stepsIndicatorStep, {
                  [styles.active]: count === currentIndex,
                })}
              ></div>
            ))}
          </div>
        </div>
        <div className={styles.popupScreenAction}>
          {currentIndex !== 0 && (
            <ButtonV3
              color="secondary"
              isFullWidth
              onClick={changeStep(currentIndex - 1)}
            >
              {tCommon('previous')}
            </ButtonV3>
          )}
          <ButtonV3
            isFullWidth
            onClick={isLastStep ? onClose : changeStep(currentIndex + 1)}
          >
            {isLastStep ? tCommon('get-your-matches') : tCommon('next')}
          </ButtonV3>
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
